import { useState, useRef } from 'react';

const STATE_COLORS = {
  none:    '#9AA4B8',
  encours: '#FFC91F',
  tire:    '#2E75B6',
  pose:    '#2E7D32',
  ping:    '#2E7D32',
};
const STATE_LABELS = {
  none: 'Non traité', encours: 'En cours', tire: 'Câble tiré', pose: 'AP posé', ping: 'Posé + pingé',
};

/**
 * Affiche un plan avec ses pins AP.
 * - aps : [{id, ref, posX, posY, state, last, zone, modele}]
 * - editable + onPlace(x,y) : clic sur le plan → placement (mode admin)
 * - onMovePin(ap, x, y) : drag d'un pin existant (mode admin)
 */
export default function PlanView({ imagePath, aps, editable, onPlace, onMovePin,
  annotations = [], annotateMode = null, annotateColor = '#C62828',
  onAddDrawing, onAddText }) {
  const wrapRef = useRef(null);
  const [selected, setSelected] = useState(null);
  const dragRef = useRef(null);
  const [drawing, setDrawing] = useState(null); // points en cours

  function relPos(e) {
    const r = wrapRef.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return {
      x: Math.min(1, Math.max(0, (t.clientX - r.left) / r.width)),
      y: Math.min(1, Math.max(0, (t.clientY - r.top) / r.height)),
    };
  }

  function handleClick(e) {
    if (annotateMode === 'text' && onAddText) {
      const { x, y } = relPos(e);
      const text = window.prompt('Texte de l\u2019annotation :');
      if (text?.trim()) onAddText(x, y, text.trim());
      return;
    }
    if (annotateMode) return;
    if (!editable || !onPlace || dragRef.current) return;
    const { x, y } = relPos(e);
    onPlace(x, y);
  }

  // Dessin libre (annotations)
  function drawStart(e) {
    if (annotateMode !== 'draw') return;
    e.preventDefault();
    setDrawing([relPos(e)]);
  }
  function drawMove(e) {
    if (annotateMode !== 'draw' || !drawing) return;
    e.preventDefault();
    setDrawing(d => [...d, relPos(e)]);
  }
  function drawEnd() {
    if (annotateMode !== 'draw' || !drawing) return;
    if (drawing.length >= 2 && onAddDrawing)
      onAddDrawing(drawing.map(p => [Math.round(p.x * 1000) / 1000, Math.round(p.y * 1000) / 1000]));
    setDrawing(null);
  }

  function startDrag(e, ap) {
    if (!editable || !onMovePin) return;
    e.stopPropagation();
    dragRef.current = ap;
  }
  function moveDrag(e) {
    if (!dragRef.current) return;
    e.preventDefault();
  }
  function endDrag(e) {
    if (!dragRef.current) return;
    const ap = dragRef.current;
    dragRef.current = null;
    const { x, y } = relPos(e.changedTouches ? { touches: e.changedTouches } : e);
    onMovePin(ap, x, y);
  }

  return (
    <div>
      <div ref={wrapRef} onClick={handleClick}
        onMouseDown={drawStart} onMouseMove={e => { moveDrag(e); drawMove(e); }}
        onMouseUp={e => { endDrag(e); drawEnd(); }} onMouseLeave={drawEnd}
        onTouchStart={drawStart} onTouchMove={e => { moveDrag(e); drawMove(e); }}
        onTouchEnd={e => { endDrag(e); drawEnd(); }}
        style={{ position:'relative', borderRadius:12, overflow:'hidden',
                 border:'1px solid #DDE4F0',
                 cursor: annotateMode === 'draw' ? 'crosshair'
                   : annotateMode === 'text' ? 'text'
                   : editable && onPlace ? 'crosshair' : 'default',
                 userSelect:'none', touchAction: (editable || annotateMode) ? 'none' : 'auto' }}>
        <img src={imagePath} alt="Plan" draggable={false}
          style={{ width:'100%', display:'block', pointerEvents:'none' }}/>
        {/* Annotations */}
        <svg viewBox="0 0 1000 1000" preserveAspectRatio="none"
          style={{ position:'absolute', inset:0, width:'100%', height:'100%', pointerEvents:'none' }}>
          {annotations.filter(a => a.kind === 'draw').map(a => (
            <polyline key={a.id}
              points={a.data.points.map(pt => `${pt[0]*1000},${pt[1]*1000}`).join(' ')}
              fill="none" stroke={a.color} strokeWidth="4"
              strokeLinecap="round" strokeLinejoin="round" opacity=".9"/>
          ))}
          {drawing && (
            <polyline points={drawing.map(pt => `${pt.x*1000},${pt.y*1000}`).join(' ')}
              fill="none" stroke={annotateColor} strokeWidth="4"
              strokeLinecap="round" strokeLinejoin="round"/>
          )}
        </svg>
        {annotations.filter(a => a.kind === 'text').map(a => (
          <div key={a.id} style={{ position:'absolute',
            left:`${a.data.x*100}%`, top:`${a.data.y*100}%`,
            transform:'translate(-50%,-110%)',
            background:a.color, color:'#fff', borderRadius:8, padding:'3px 8px',
            fontSize:11, fontWeight:700, maxWidth:180, pointerEvents:'none',
            boxShadow:'0 1px 5px rgba(0,0,0,.35)' }}>
            {a.data.text}
          </div>
        ))}
        {aps.filter(a => a.posX != null).map(ap => (
          <button key={ap.id} title={`${ap.ref} — ${STATE_LABELS[ap.state] || ''}`}
            onMouseDown={e => startDrag(e, ap)}
            onTouchStart={e => startDrag(e, ap)}
            onClick={e => { e.stopPropagation(); setSelected(s => s?.id === ap.id ? null : ap); }}
            style={{
              position:'absolute', left:`${ap.posX*100}%`, top:`${ap.posY*100}%`,
              transform:'translate(-50%,-50%)',
              width:18, height:18, borderRadius:'50%',
              background: STATE_COLORS[ap.state] || '#9AA4B8',
              border: ap.state === 'ping' ? '3px solid #FFC91F' : '2.5px solid #fff',
              boxShadow:'0 1px 4px rgba(19,38,77,.45)',
              cursor: editable ? 'grab' : 'pointer', padding:0,
            }}/>
        ))}
        {selected && selected.posX != null && (
          <div style={{
            position:'absolute',
            left:`min(max(${selected.posX*100}%, 90px), calc(100% - 90px))`,
            top:`${selected.posY*100}%`,
            transform:'translate(-50%, calc(-100% - 14px))',
            background:'#13264D', color:'#fff', borderRadius:10, padding:'8px 12px',
            fontSize:12, width:180, zIndex:5, boxShadow:'0 4px 14px rgba(0,0,0,.3)',
          }}>
            <p style={{ fontWeight:800 }}>{selected.ref}</p>
            <p style={{ opacity:.85 }}>{selected.zone} · {selected.modele}</p>
            <p style={{ color: '#FFC91F', fontWeight:700, marginTop:2 }}>
              {STATE_LABELS[selected.state]}
            </p>
            {selected.last && (
              <p style={{ opacity:.75 }}>Dernier passage : {selected.last.report.semaine} {selected.last.report.numJour}</p>
            )}
          </div>
        )}
      </div>
      <div style={{ display:'flex', gap:14, flexWrap:'wrap', marginTop:8 }}>
        {Object.entries(STATE_LABELS).map(([k, l]) => (
          <span key={k} style={{ display:'flex', alignItems:'center', gap:5, fontSize:12, color:'#5A6478' }}>
            <span style={{ width:11, height:11, borderRadius:'50%', background:STATE_COLORS[k],
              border: k==='ping' ? '2px solid #FFC91F' : '1.5px solid #fff',
              boxShadow:'0 0 0 1px #DDE4F0' }}/>
            {l}
          </span>
        ))}
      </div>
    </div>
  );
}
